/* eslint-disable jsx-a11y/alt-text */
import * as React from 'react'
import ChatbotWrapper from '../../../components/ChatbotWrapper'
import styles from './styles.module.scss'
import TwoColumn from '../../../components/TwoColumn'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTwitch } from '@fortawesome/free-brands-svg-icons'
import { faMusic, faDownload, faClock, faSitemap, faMicrophone, faSliders, faKeyboard } from '@fortawesome/free-solid-svg-icons'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { ReactComponent as OBSLogo } from '../assets/obs.svg'
import { ReactComponent as VTSLogo } from '../assets/vts.svg'
import Section from '../../../components/Section'
import List from '../../../components/List'
import DownloadAndDiscordButtons from '../../../components/DownloadAndDiscordButtons'
import DiscordLink from '../../../components/DiscordLink'
import { RainbowText } from '../../../components/RainbowText'

type Props = {}

const DracoBot: React.FC<React.PropsWithChildren<{ reverse?: boolean; light?: boolean }>> = ({ reverse = true, light = true }) => {
  return <RainbowText light={light} reverse={reverse} text='Draco.Bot' />
}

const Features: React.FC<Props> = () => {
  return (
    <ChatbotWrapper
      decorate
      containerWrap={false}
      title='Features'
      titleButtons={
        <div>
          <a href='#chat-events' title='Chat & Twitch Events' className={styles.iconButton}>
            <FontAwesomeIcon icon={faTwitch as IconDefinition} />
          </a>
          <a href='#obs' title='OBS Studio Control' className={styles.iconButton}>
            <OBSLogo />
          </a>
          <a href='#vts' title='VTube Studio Integration' className={styles.iconButton}>
            <VTSLogo />
          </a>
          <a href='#audio' title='Audio, Music & TTS' className={styles.iconButton}>
            <FontAwesomeIcon icon={faMusic as IconDefinition} />
          </a>
          <a href='#timers-counters' title='Timers & Counters' className={styles.iconButton}>
            <FontAwesomeIcon icon={faClock as IconDefinition} />
          </a>
          <a href='#bot-controls' title='Bot Controls' className={styles.iconButton}>
            <FontAwesomeIcon icon={faSliders as IconDefinition} />
          </a>
          <a href='#advanced' title='Logic & Automation' className={styles.iconButton}>
            <FontAwesomeIcon icon={faSitemap as IconDefinition} />
          </a>
          <a href='#voice' title='Voice Commands' className={styles.iconButton}>
            <FontAwesomeIcon icon={faMicrophone as IconDefinition} />
          </a>
          <a href='#utility' title='Local Control & Utility' className={styles.iconButton}>
            <FontAwesomeIcon icon={faKeyboard as IconDefinition} />
          </a>
        </div>
      }
      header={
        <>
          <p className='display-6'>
            <DracoBot /> gives your stream one place to automate chat, Twitch events, OBS, audio, and on-screen moments, so everything can react together without a pile of separate tools.
          </p>
          <p className='display-6'>
            It runs locally on your machine for fast, dependable control during real streams. From simple chat responses to more advanced multi-step automation, <DracoBot /> helps your setup feel
            smooth, responsive, and easier to manage live. Want ideas, help, or updates? Come hang out in our <DiscordLink />.
          </p>
          <div className={styles.buttons}>
            <DownloadAndDiscordButtons />
          </div>
        </>
      }
    >
      <div className={styles.container}>
        <Section containerClassName={styles.noSmallGap} id='chat-events' title='Chat & Twitch Events' icon={<FontAwesomeIcon icon={faTwitch as IconDefinition} className={styles.icon} />}>
          <p className='display-6'>
            Keep your stream responsive without having to micromanage every moment. <DracoBot /> can react to chat, channel point redemptions, raids, subs, ads, and other Twitch activity with the
            right response at the right time.
          </p>

          <TwoColumn className={styles.subsection}>
            <List title='React to Live Activity' items={['Chat messages', 'Channel point redemptions', 'Raids', 'Subscriptions and gift subs', 'Ads and other Twitch events']} />
            <List
              className={styles.second}
              title='Do More Than Reply'
              items={[
                'Send chat messages',
                'Post Twitch announcements',
                'Create shoutouts',
                'Update your stream title',
                'Change your category or game',
                'Add or remove stream tags',
                'Run ads',
                'Enable, disable, pause, or unpause channel point rewards',
              ]}
            />
          </TwoColumn>

          <List className={styles.third} title='Adjust Chat Modes Automatically' items={['Emote-only mode', 'Follower-only mode', 'Slow mode', 'Subscriber-only mode']} />

          <p className={styles.reasoning}>Your stream stays active and polished, even when your focus is somewhere else.</p>
        </Section>

        <Section containerClassName={styles.noSmallGap} id='obs' title='OBS Studio Control' icon={<OBSLogo className={styles.icon} />}>
          <p className='display-6'>
            Let OBS react as naturally as the rest of your stream. <DracoBot /> can control scenes, sources, filters, audio, and more from chat commands, rewards, timers, hotkeys, and stream events.
          </p>
          <p className='display-6'>
            <DracoBot /> requires <b>OBS Studio v28+</b>.
          </p>

          <TwoColumn className={styles.subsection}>
            <List
              title='Scenes, Sources & Filters'
              items={[
                'Switch to a specific scene',
                'Return to the previous scene',
                'Show or hide sources',
                'Enable or disable filters',
                'Move sources to set positions',
                'Randomize source positions for surprise effects',
              ]}
            />
            <List
              className={styles.second}
              title='Update OBS Live'
              items={['Change text source content', 'Change browser source URLs', 'Swap image sources', 'Swap media files', 'Adjust audio volume', 'Mute or unmute audio sources']}
            />
          </TwoColumn>

          <List className={styles.third} title='For Advanced Setups' items={['Trigger custom OBS requests for even more control']} />

          <p className={styles.reasoning}>No alt-tabbing, no scrambling, no missed moments. OBS changes when your stream does.</p>
        </Section>

        <Section containerClassName={styles.noSmallGap} id='vts' title='VTube Studio Integration' icon={<VTSLogo className={styles.icon} />}>
          <p className='display-6'>
            Make your avatar part of the experience instead of a separate thing to manage. <DracoBot /> can trigger VTube Studio actions from the same automations that already run the rest of your
            stream.
          </p>

          <List title='VTube Studio Actions' items={['Switch models', 'Move your model', 'Trigger hotkeys', 'Coordinate movement with other actions']} />

          <p className={styles.reasoning}>Your model can react right alongside chat, rewards, timers, and stream events.</p>
        </Section>

        <Section containerClassName={styles.noSmallGap} id='audio' title='Audio, Music & Text-to-Speech' icon={<FontAwesomeIcon icon={faMusic as IconDefinition} className={styles.icon} />}>
          <p className='display-6'>
            Audio should feel like part of the show, not a separate system to babysit. <DracoBot /> brings music, sound effects, and text-to-speech into the same automation flow as everything else.
          </p>

          <TwoColumn className={styles.subsection}>
            <List title='Music & Sound' items={['Play sound effects', 'Start or pause playlist playback', 'Switch between playlists', 'Trigger sounds as part of larger automations']} />
            <List
              className={styles.second}
              title='Text-to-Speech'
              items={['Speak messages through TTS', 'Use modern voices or retro-style speech', 'Include TTS in commands, events, and action chains']}
            />
          </TwoColumn>

          <p className={styles.reasoning}>One automation system, one workflow, and audio that reacts right on cue.</p>
        </Section>

        <Section containerClassName={styles.noSmallGap} id='timers-counters' title='Timers & Counters' icon={<FontAwesomeIcon icon={faClock as IconDefinition} className={styles.icon} />}>
          <p className='display-6'>
            Some stream moments are not one-and-done. <DracoBot /> gives you the tools to track progress, carry state forward, and build interactions that last beyond a single trigger.
          </p>

          <TwoColumn className={styles.subsection}>
            <List title='Counters' items={['Increase counters', 'Decrease counters', 'Reset counters']} />
            <List className={styles.second} title='Timers' items={['Start timers', 'Stop timers', 'Reset timers']} />
          </TwoColumn>

          <p className={styles.reasoning}>Great for milestones, challenge runs, recurring events, subathons, and any stream with moving parts.</p>
        </Section>

        <Section
          containerClassName={styles.noSmallGap}
          id='bot-controls'
          title='Bot Controls & Automation Management'
          icon={<FontAwesomeIcon icon={faSliders as IconDefinition} className={styles.icon} />}
        >
          <p className='display-6'>
            <DracoBot /> can do more than trigger actions. It can also control the systems behind your stream while you are live, making it easier to adapt without interrupting the flow.
          </p>

          <TwoColumn className={styles.subsection}>
            <List title='Turn Features On or Off Mid-Stream' items={['Chat commands', 'Timed announcements', 'Event responses', 'Greetings', 'Hotkeys', 'Sounds', 'Voice commands']} />
            <List className={styles.second} title='Run Saved Automation' items={['Run a saved action set', 'Trigger a saved command directly']} />
          </TwoColumn>

          <p className={styles.reasoning}>Stay flexible live without digging through menus or reworking your setup on the fly.</p>
        </Section>

        <Section containerClassName={styles.noSmallGap} id='advanced' title='Logic & Automation Flow' icon={<FontAwesomeIcon icon={faSitemap as IconDefinition} className={styles.icon} />}>
          <p className='display-6'>
            Simple automation is useful. Smart automation is where things get fun. <DracoBot /> gives you tools to chain actions together, control timing, check conditions, and store values so your
            setup can respond more intelligently.
          </p>

          <TwoColumn className={styles.subsection}>
            <List title='Control the Flow' items={['Delay actions', 'Group actions together', 'Pick a random action from a list', 'Run actions only when conditions match']} />
            <List className={styles.second} title='Store and Output Values' items={['Set variables', 'Reset variables', 'Write text to output files']} />
          </TwoColumn>

          <List
            className={styles.third}
            title='Conditions Can Check Things Like'
            items={['Text matches', 'Numbers and comparisons', 'Dates and times', 'Stored variables', 'Selected OBS values and stream state']}
          />

          <p className={styles.reasoning}>This is what turns a collection of actions into a stream setup that feels deliberate.</p>
        </Section>

        <Section containerClassName={styles.noSmallGap} id='voice' title='Voice Commands' icon={<FontAwesomeIcon icon={faMicrophone as IconDefinition} className={styles.icon} />}>
          <p className='display-6'>
            When your hands are busy, voice control can keep things moving. <DracoBot /> can work spoken commands into your automation setup so certain actions are easier to trigger live.
          </p>

          <List
            title='Voice Control'
            items={['Enable or disable voice command handling as part of your automation setup', 'Use spoken commands to trigger stream actions', 'Fit voice control into larger live workflows']}
          />

          <p className={styles.reasoning}>Hands-free control when timing matters.</p>
        </Section>

        <Section containerClassName={styles.noSmallGap} id='utility' title='Local Control & Utility' icon={<FontAwesomeIcon icon={faKeyboard as IconDefinition} className={styles.icon} />}>
          <p className='display-6'>
            Some of the most useful stream tools are the ones that quietly connect everything else. <DracoBot /> includes utility actions that help bridge gaps between apps, workflows, and those
            little moments that still matter live.
          </p>

          <List title='Utility Actions' items={['Trigger keyboard shortcuts', 'Show in-app notifications']} />

          <p className={styles.reasoning}>Useful glue for the moments between your bigger automations.</p>
        </Section>

        <Section
          containerClassName={styles.noSmallGap}
          noAlt
          contentContainerClassName={styles.buttons}
          title='Spend more time actually streaming'
          icon={<FontAwesomeIcon icon={faDownload as IconDefinition} className={styles.icon} />}
        >
          <p className='display-6'>
            <DracoBot /> brings your chat, OBS, audio, and automation tools together in one place, so your stream feels easier to run live.
          </p>
          <p className='display-6'>It&apos;s built for streamers who want more control, less friction, and fewer moving parts to worry about mid-broadcast.</p>
          <DownloadAndDiscordButtons />
        </Section>
      </div>
    </ChatbotWrapper>
  )
}

export default Features
